import { SagaIterator } from 'redux-saga';
import { call, put, select, takeLatest } from 'redux-saga/effects';

import { ConnectedApplications } from '~src/connectedApplications/resources/ConnectedApplicationsResource';
import { DataConnections } from '~src/datasource/dataConnection/resources/DataConnections';
import { RunFlowTaskInfo } from '~src/flow/models/RunFlowTaskInfo';
import { RunFlowTasks } from '~src/flow/resources/RunFlowTasks';
import { LinkedTasks } from '~src/linkedTasks/resources/LinkedTasks';
import { PublishedConnectionTables } from '~src/publishedConnectionTable/resources/PublishedConnectionTables';
import { ResourceGovernanceService } from '~src/schedule/types/ResourceGovernanceService';
import { DeleteForeverAction } from '~src/spacesPlatform/components/ActionsMenu/EntityActions/DeleteForeverAction';
import { RestoreAction } from '~src/spacesPlatform/components/ActionsMenu/EntityActions/RestoreAction';
import { selectSpaceInfo } from '~src/spacesPlatform/selectors/SpaceInfoSelectors';

import { BackgroundJobsService } from '../../backgroundJob/types/BackgroundJobs';
import { AvailableActionsForEntityRequest } from '../../bootstrap/redux/actions/AppActionTypes';
import { requestFailed } from '../../bootstrap/redux/actions/RequestFailedAction';
import { SessionServerSelectors } from '../../bootstrap/redux/selectors/SessionInfoSelectors';
import { Databases } from '../../catalog/resources/Databases';
import { Tables } from '../../catalog/resources/Tables';
import { Messages } from '../../codegen/Localize';
import { getCommonDatasourceInfo } from '../../datasource/models/mappers/CommonDatasourceInfo';
import { DataSources } from '../../datasource/resources/Datasources';
import { ContentDraftStatus } from '../../drafts/serverApi/types/ContentDraft';
import { Flows } from '../../flow/resources/Flows';
import { IActionResource } from '../../httpClient/resources/Resource';
import { Projects } from '../../projects/resources/Projects';
import { PublishedConnections } from '../../publishedConnection/resources/PublishedConnections';
import { Views } from '../../views/resources/Views';
import { CustomViews } from '../../views/ServerApi/requests/CustomViews/CustomViews';
import { Workbooks } from '../../workbook/resources/Workbooks';
import { availableActionForEntityRequestSucceeded, AvailableActionForEntityRequestSucceededAction, AvailableActionsForEntityRequestAction } from '../actions/AvailableActionsForEntityActions';
import { ExplorableItemType } from '../models/ContentType';
import { IContentInfo } from '../models/IContentInfo';
import { IExplorableItemTypeInfo } from '../models/resourceInfoParts';
import { AvailableActions } from '../state/AvailableActionsForEntityState';
import { isRecycleBinSpace, SpaceInfo } from '../state/SpaceInfo';
import { getCorrectEntityIdForActions } from './SpacesSagaHelpers';

const GetActionResourceForContentType: Partial<Record<ExplorableItemType, () => IActionResource<AvailableActions>>> = {
  workbook: () => Workbooks,
  project: () => Projects,
  datasource: () => DataSources,
  view: () => Views,
  connectedApplication: () => ConnectedApplications,
  dataconnection: () => DataConnections,
  flow: () => Flows,
  backgroundJob: () => BackgroundJobsService,
  table: () => Tables,
  database: () => Databases,
  virtualConnection: () => PublishedConnections,
  virtualConnectionTable: () => PublishedConnectionTables,
  customView: () => CustomViews,
  resourceGovernanceSetting: () => ResourceGovernanceService,
  runFlowTask: () => RunFlowTasks,
};

function getResourceContentId(
  entityType: ExplorableItemType,
  entityId: string,
  spaceInfo: SpaceInfo,
): string {
  if (entityType === 'extractTask') {
    return spaceInfo.item.id;
  }
  return entityId;
}

function getResourceContentType(
  entityType: ExplorableItemType,
  spaceInfo: SpaceInfo
): ExplorableItemType {
  if (entityType === 'extractTask') {
    return spaceInfo.item.contentType as ExplorableItemType;
  }
  return entityType;
}

/**
 * We request actions only for the ExplorableItemTypes that have an API call.
 * For other types there aren't APIs that retrieve the allowed actions;
 * instead, we determine allowed actions by looking at properties of those items and the site role.
 */
export function fetchAvailableActionsForEntity(
  entityType: ExplorableItemType,
  entityId: string,
  spaceInfo: SpaceInfo
): Promise<AvailableActions> {
  const resourceContentId = getResourceContentId(entityType, entityId, spaceInfo);
  const resourceContentType = getResourceContentType(entityType, spaceInfo);
  const actionResourceGetter = GetActionResourceForContentType[resourceContentType];
  const actionResource = actionResourceGetter && actionResourceGetter();
  if (actionResource) {
    return actionResource.actions(resourceContentId);
  }
  return null;
}

function fetchAvailableActionsForFlowDraft(draftId: string): Promise<AvailableActions> {
  return Flows.draftActions(draftId);
}

function fetchAvailableActionsForLinkedTask(id: string): Promise<AvailableActions> {
  return LinkedTasks.actions(id);
}

// undefined uses the resource API; null uses permissions already supplied by the item metadata.
export type AvailableActionsResolver = (item: IExplorableItemTypeInfo) => AvailableActionForEntityRequestSucceededAction['availableActions'] | null | undefined;

export function* processAvailableActionsForEntityRequest(
  action: AvailableActionsForEntityRequestAction,
  resolveAvailableActions?: AvailableActionsResolver,
): SagaIterator {
  try {
    const availableActions = resolveAvailableActions?.(action.item);
    if (availableActions !== undefined) {
      yield put(availableActionForEntityRequestSucceeded(action.item.contentType, action.item.id, availableActions));
      return;
    }
    const spaceInfo = yield select(selectSpaceInfo);
    const commonDatasource = getCommonDatasourceInfo(action.item as IContentInfo<'datasource'>);

    // When admin profiles is OFF, return null to use old permission system (canManageUsers, etc.)
    if (action.item.contentType === 'siteuser') {
      const isAdminProfilesEnabled: boolean = yield select(SessionServerSelectors.isAdminProfilesEnabledOnServer);
      if (!isAdminProfilesEnabled) {
        yield put(availableActionForEntityRequestSucceeded(action.item.contentType, action.item.id, null));
        return;
      }
    }

    if (spaceInfo && (isRecycleBinSpace(spaceInfo))) {
      // In RecycleBin space we always return restore and DeleteForever action
      // There is no API to call the available actions for RecycleBin.
      const availableActions: AvailableActions = {
        recycleBinActions: [RestoreAction, DeleteForeverAction]
      };
      yield put(availableActionForEntityRequestSucceeded(action.item.contentType, action.item.id, availableActions));
      return;
    }

    if (action.item.contentType === 'datasource' && DataSources.isEmbedded(commonDatasource)) {
      yield put(availableActionForEntityRequestSucceeded(action.item.contentType, action.item.id, null));
    } else if (action.item.contentType === 'flow'
      && (action.item as IContentInfo<'flow'>).draftStatus === ContentDraftStatus.unpublished) {
      // flow drafts that have never been published need to call getFlowDraftActions instead of getFlowActions
      const availableActions = yield call(fetchAvailableActionsForFlowDraft, action.item.id);
      yield put(availableActionForEntityRequestSucceeded(action.item.contentType, action.item.id, availableActions));
    } else if (action.item.contentType === 'runFlowTask') {
        const runFlowTask = action.item as RunFlowTaskInfo;
        if (runFlowTask.taskType === 'FlowTask') {
          // use the flowId of the task
          const availableActions = yield call(fetchAvailableActionsForEntity, action.item.contentType, runFlowTask.flowId,
            spaceInfo);
          yield put(availableActionForEntityRequestSucceeded(action.item.contentType, action.item.id, availableActions));
        } else if (runFlowTask.taskType === 'LinkedTask') {
          // call separate API for linked task action permissions
          const availableActions = yield call(fetchAvailableActionsForLinkedTask, runFlowTask.linkedTaskId);
          yield put(availableActionForEntityRequestSucceeded(action.item.contentType, action.item.id, availableActions));
        }
    } else {
      let entityIdForActions: string = yield call(getCorrectEntityIdForActions, action.item.contentType, action.item.id);

      // Hack to use `vizportalId` field for Database & Table
      // TFS912863: Remove this hack once we switched to use vizportalId as primary ID for database & table
      if (action.item.contentType === 'table' || action.item.contentType === 'database') {
        entityIdForActions = action.item.vizportalId;
      }

      const availableActions = yield call(fetchAvailableActionsForEntity, action.item.contentType, entityIdForActions,
          spaceInfo);
      yield put(availableActionForEntityRequestSucceeded(action.item.contentType, action.item.id, availableActions));
    }
  } catch(e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function* rootAvailableActionsForEntitySaga(resolveAvailableActions?: AvailableActionsResolver): SagaIterator {
  yield takeLatest(AvailableActionsForEntityRequest, (action: AvailableActionsForEntityRequestAction) =>
    processAvailableActionsForEntityRequest(action, resolveAvailableActions));
}
